import React, { useContext, useMemo } from "react";
import PropTypes from "prop-types";
import { Lock, Mail, ArrowLeft, ArrowRight, ShieldAlert, ChevronLeft, ChevronRight } from "lucide-react";
import { ThemeContext } from "../constant/theme";
import { LanguageContext } from "../constant/LanguageContext";

/* ----------------------------- Translations ----------------------------- */
const STRINGS = {
  en: {
    fallbackPage: "this page",
    eyebrow: "403 · Restricted",
    title: "Access Denied",
    lineOne: (page) => `You don't have permission to view ${page}.`,
    lineTwo: "Need help? Contact your administrator to request access.",
    lead: (page) =>
      `It looks like your account doesn't have the right permissions for ${page}.`,
    description:
      "This area is reserved for specific roles. Your account doesn't currently meet the requirements, so the content behind this page is hidden. Reach out to your administrator if you believe this is a mistake.",
    requiredRoleLabel: "Required role",
    yourRoleLabel: "Your role",
    unsetRole: "Unassigned",
    referenceLabel: "Reference",
    back: "Go Back",
    contact: "Contact Administrator",
    contactSubject: (page) => `Access request — ${page}`,
    contactBody: (page) =>
      `Hello,\n\nI'd like to request access to "${page}".\nCould you let me know the steps required?\n\nThank you.`,
  },
  ar: {
    fallbackPage: "هذه الصفحة",
    eyebrow: "403 · محظور",
    title: "غير مصرح بالوصول",
    lineOne: (page) => `ليس لديك صلاحية الوصول إلى ${page}.`,
    lineTwo: "هل تحتاج مساعدة؟ تواصل مع المسؤول لطلب الوصول.",
    lead: (page) =>
      `يبدو أن حسابك لا يملك الصلاحيات اللازمة للوصول إلى ${page}.`,
    description:
      "هذه المنطقة محجوزة لأدوار محددة. حسابك الحالي لا يستوفي المتطلبات، لذا محتوى هذه الصفحة مخفي. تواصل مع المسؤول إذا كنت تعتقد أن هذا خطأ.",
    requiredRoleLabel: "الدور المطلوب",
    yourRoleLabel: "دورك",
    unsetRole: "غير مُعيَّن",
    referenceLabel: "مرجع",
    back: "رجوع",
    contact: "تواصل مع المسؤول",
    contactSubject: (page) => `طلب صلاحية — ${page}`,
    contactBody: (page) =>
      `السلام عليكم،\n\nأرغب في طلب صلاحية الوصول إلى "${page}".\nيرجى إبلاغي بالخطوات المطلوبة.\n\nشكرًا.`,
  },
};

/* ------------------------- Style factory (memoized) ---------------------- */
const buildStyles = (isDark) => {
  const c = {
    bg: isDark ? "rgba(30, 41, 59, 0.65)" : "rgba(255, 255, 255, 0.92)",
    border: isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.06)",
    title: isDark ? "#ffffff" : "#0f172a",
    body: isDark ? "rgba(255,255,255,0.72)" : "rgba(15,23,42,0.66)",
    muted: isDark ? "rgba(255,255,255,0.45)" : "rgba(15,23,42,0.48)",
    softBg: isDark ? "rgba(148, 163, 184, 0.08)" : "rgba(15,23,42,0.04)",
    chipBg: isDark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.05)",
    chipBorder: isDark ? "rgba(255,255,255,0.12)" : "rgba(15,23,42,0.09)",
    accent: "#ef4444",
    accentSoft: isDark ? "rgba(239,68,68,0.18)" : "rgba(239,68,68,0.10)",
    accentBorder: isDark ? "rgba(239,68,68,0.40)" : "rgba(239,68,68,0.28)",
    accentSolid: isDark ? "#f87171" : "#dc2626",
    accentShadow: isDark
      ? "rgba(239,68,68,0.35)"
      : "rgba(239,68,68,0.30)",
  };

  return {
    container: {
      width: "100%",
      minHeight: "100vh",
      boxSizing: "border-box",
      padding: "3.5rem 2.5rem",
      textAlign: "center",
      borderRadius: "24px",
      background: c.bg,
      backdropFilter: "blur(18px)",
      WebkitBackdropFilter: "blur(18px)",
      border: `1px solid ${c.border}`,
      boxShadow: isDark
        ? "0 10px 40px rgba(0,0,0,0.35)"
        : "0 10px 40px rgba(0,0,0,0.08)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "20px",
    },
    eyebrow: {
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      padding: "5px 12px",
      borderRadius: "999px",
      fontSize: "11px",
      fontWeight: 700,
      letterSpacing: "0.10em",
      textTransform: "uppercase",
      color: c.accentSolid,
      background: c.accentSoft,
      border: `1px solid ${c.accentBorder}`,
    },
    badge: {
      width: "92px",
      height: "92px",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: c.accentSoft,
      border: `1.5px solid ${c.accentBorder}`,
      boxShadow: isDark
        ? `0 0 0 10px rgba(239,68,68,0.07), 0 8px 24px ${c.accentShadow}`
        : `0 0 0 10px rgba(239,68,68,0.05), 0 8px 24px ${c.accentShadow}`,
      animation: "pulse 2.4s ease-in-out infinite",
    },
    title: {
      margin: 0,
      fontSize: "26px",
      fontWeight: 800,
      letterSpacing: "-0.015em",
      lineHeight: 1.2,
      color: c.title,
    },
    lead: {
      margin: 0,
      maxWidth: "540px",
      fontSize: "15px",
      lineHeight: 1.6,
      color: c.body,
    },
    descriptionBox: {
      maxWidth: "580px",
      padding: "14px 18px",
      borderRadius: "14px",
      background: c.softBg,
      border: `1px solid ${c.chipBorder}`,
      fontSize: "13.5px",
      lineHeight: 1.7,
      color: c.muted,
    },
    infoRow: {
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "center",
      alignItems: "center",
      gap: "10px",
      maxWidth: "580px",
    },
    chip: {
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      padding: "7px 12px",
      borderRadius: "10px",
      fontSize: "12.5px",
      fontWeight: 600,
      background: c.chipBg,
      border: `1px solid ${c.chipBorder}`,
      color: c.body,
    },
    chipLabel: {
      fontWeight: 500,
      color: c.muted,
      letterSpacing: "0.01em",
    },
    chipValue: {
      fontWeight: 700,
      color: c.title,
    },
    actions: {
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "center",
      gap: "12px",
      marginTop: "6px",
    },
    btnSecondary: {
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      padding: "11px 20px",
      fontSize: "14px",
      fontWeight: 600,
      borderRadius: "12px",
      cursor: "pointer",
      transition: "transform .15s ease, background .15s ease, border-color .15s ease",
      background: "transparent",
      color: c.body,
      border: `1px solid ${c.chipBorder}`,
      textDecoration: "none",
      fontFamily: "inherit",
    },
    btnPrimary: {
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      padding: "11px 20px",
      fontSize: "14px",
      fontWeight: 700,
      borderRadius: "12px",
      cursor: "pointer",
      transition: "transform .15s ease, box-shadow .15s ease, background .15s ease",
      background: c.accent,
      color: "#ffffff",
      border: `1px solid ${c.accent}`,
      boxShadow: `0 6px 18px ${c.accentShadow}`,
      textDecoration: "none",
      fontFamily: "inherit",
    },
    footer: {
      marginTop: "6px",
      fontSize: "12.5px",
      lineHeight: 1.6,
      color: c.muted,
      maxWidth: "500px",
    },
  };
};

const ANIMATION_STYLES = `
@keyframes pulseBadge {
  0%, 100% { transform: scale(1); }
  50%      { transform: scale(1.04); }
}
`;

/* ----------------------------- Subcomponents ---------------------------- */
const LockBadge = () => (
  <>
    <style>{ANIMATION_STYLES}</style>
    <Lock size={38} color="#ef4444" strokeWidth={2.2} />
  </>
);

const Button = ({ as: Tag = "button", style, children, ...rest }) => (
  <Tag style={style} {...rest}>
    {children}
  </Tag>
);
Button.propTypes = {
  as: PropTypes.elementType,
  style: PropTypes.object,
  children: PropTypes.node,
};

/* ------------------------------- Component ------------------------------ */
/**
 * AccessDenied — full-page content panel shown when a page is
 * VISIBLE_ACCESS_DENIED for the current user (nav item visible, page blocked).
 * Not a modal: renders in place of the page content.
 *
 * Usage:
 *   <AccessDenied
 *     pageLabel="Payroll"
 *     reason="Limited to HR Managers"          // optional, overrides default description
 *     requiredRole="HR Manager"               // optional
 *     currentRole="Employee"                  // optional
 *     adminEmail="admin@example.com"          // optional; enables mailto link
 *     reference="ADM-2024-001"                // optional support reference
 *     onBack={() => navigate(-1)}             // optional back handler
 *     onContact={() => openSupport()}         // only used if no adminEmail
 *   />
 */
const AccessDenied = ({
  pageLabel,
  reason,
  requiredRole,
  currentRole,
  adminEmail,
  reference,
  onBack,
  onContact,
}) => {
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const isDark = theme === "dark";
  const isRTL = language === "ar";
  const t = STRINGS[isRTL ? "ar" : "en"];

  const styles = useMemo(() => buildStyles(isDark), [isDark]);

  const containerDir = isRTL ? "rtl" : "ltr";
  const pageName = pageLabel || t.fallbackPage;
  const BackArrow = isRTL ? ChevronRight : ChevronLeft;
  // BackArrow wrapper: ArrowLeft is also imported for fallback usage, but Chevron
  // pairs better with RTL rendering on both browsers.

  const mailHref = useMemo(() => {
    if (!adminEmail) return null;
    const subject = encodeURIComponent(t.contactSubject(pageName));
    const body = encodeURIComponent(t.contactBody(pageName));
    return `mailto:${adminEmail}?subject=${subject}&body=${body}`;
  }, [adminEmail, t, pageName]);

  const handleBack = (e) => {
    if (onBack) {
      e.preventDefault();
      onBack();
      return;
    }
    if (typeof window !== "undefined" && window.history.length > 1) {
      e.preventDefault();
      window.history.back();
    }
  };

  const hasRoles = Boolean(requiredRole || currentRole);

  return (
    <div dir={containerDir} style={styles.container}>
      {/* Eyebrow tag */}
      {/* لا 7
       */}

      {/* Icon badge with animated glow */}
      <div
        style={{
          ...styles.badge,
          animationName: "pulseBadge",
        }}
      >
        <LockBadge />
      </div>

      <h2 style={styles.title}>{t.title}</h2>

      <p style={styles.lead}>{t.lead(pageName)}</p>

      {/* Reason / description block */}
      {(reason || t.description) && (
        <div style={styles.descriptionBox}>{reason || t.description}</div>
      )}

      {/* Role info chips */}
      {hasRoles && (
        <div style={styles.infoRow}>
          {requiredRole && (
            <span style={styles.chip}>
              <span style={styles.chipLabel}>{t.requiredRoleLabel}</span>
              <span style={styles.chipValue}>·</span>
              <span style={styles.chipValue}>{requiredRole}</span>
            </span>
          )}
          {currentRole && (
            <span style={styles.chip}>
              <span style={styles.chipLabel}>{t.yourRoleLabel}</span>
              <span style={styles.chipValue}>·</span>
              <span style={styles.chipValue}>{currentRole}</span>
            </span>
          )}
        </div>
      )}

      {/* Reference code */}
      {reference && (
        <div style={styles.infoRow}>
          <span
            style={{
              ...styles.chip,
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              letterSpacing: "0.02em",
            }}
          >
            <span style={styles.chipLabel}>{t.referenceLabel}</span>
            <span style={styles.chipValue}>·</span>
            <span style={styles.chipValue}>{reference}</span>
          </span>
        </div>
      )}

      {/* Actions */}
      {/* <div style={styles.actions}>
        <Button
          as="a"
          href="#"
          onClick={handleBack}
          style={styles.btnSecondary}
          aria-label={t.back}
        >
          <BackArrow size={16} />
          {t.back}
        </Button>

        {mailHref ? (
          <Button
            as="a"
            href={mailHref}
            style={styles.btnPrimary}
            aria-label={t.contact}
          >
            <Mail size={16} />
            {t.contact}
          </Button>
        ) : (
          <Button
            type="button"
            onClick={onContact}
            style={styles.btnPrimary}
            aria-label={t.contact}
            disabled={!onContact}
          >
            <Mail size={16} />
            {t.contact}
          </Button>
        )}
      </div> */}

      <p style={styles.footer}>{t.lineTwo}</p>
    </div>
  );
};

AccessDenied.propTypes = {
  pageLabel: PropTypes.string,
  reason: PropTypes.string,
  requiredRole: PropTypes.string,
  currentRole: PropTypes.string,
  adminEmail: PropTypes.string,
  reference: PropTypes.string,
  onBack: PropTypes.func,
  onContact: PropTypes.func,
};

AccessDenied.defaultProps = {
  pageLabel: undefined,
  reason: undefined,
  requiredRole: undefined,
  currentRole: undefined,
  adminEmail: undefined,
  reference: undefined,
  onBack: undefined,
  onContact: undefined,
};

export default AccessDenied;
